import React, { useState, useEffect } from 'react';

interface ScamPopupProps {
  onClose: () => void;
}

const ScamPopup: React.FC<ScamPopupProps> = ({ onClose }) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [name, setName] = useState('');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showSecondPopup, setShowSecondPopup] = useState(false);
  const [showThirdPopup, setShowThirdPopup] = useState(false);

  // Add a slight random position offset when component mounts
  useEffect(() => {
    const randomX = Math.floor(Math.random() * 100) - 50;
    const randomY = Math.floor(Math.random() * 100) - 50;
    setPosition({ x: randomX, y: randomY });
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`WARNING: Your computer has been infected with 37 viruses! Call our support line immediately!\n\nCard details captured:\nName: ${name}\nCard: ${cardNumber}\nExpiry: ${expiryDate}\nCVV: ${cvv}`);
    onClose();
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // 80% chance to show a secondary popup when trying to close
    if (Math.random() < 0.8 && !showSecondPopup) {
      setShowSecondPopup(true);
      // Then 50% chance to show a third popup
      if (Math.random() < 0.5) {
        setTimeout(() => setShowThirdPopup(true), 500);
      }
    } else {
      onClose();
    }
  };

  // Windows XP style popup
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Main popup */}
      <div 
        className="w-[500px] shadow-xl overflow-hidden"
        style={{ 
          transform: `translate(${position.x}px, ${position.y}px)`,
          position: 'absolute',
          zIndex: 51
        }}
      >
        {/* Window header - classic Windows blue gradient */}
        <div className="flex items-center justify-between bg-gradient-to-r from-blue-700 to-blue-500 px-2 py-1 text-white select-none cursor-default">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-500 mr-2 rounded-full"></div>
            <span className="text-sm font-bold">Security Alert - Windows Protection</span>
          </div>
          <div className="flex">
            <button className="w-5 h-5 flex items-center justify-center mx-1 bg-gradient-to-b from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 border border-gray-400 text-black font-bold">_</button>
            <button className="w-5 h-5 flex items-center justify-center mx-1 bg-gradient-to-b from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 border border-gray-400 text-black font-bold">▢</button>
            <button 
              onClick={handleCloseClick}
              className="w-5 h-5 flex items-center justify-center mx-1 bg-gradient-to-b from-red-400 to-red-600 hover:from-red-500 hover:to-red-700 border border-red-700 text-white font-bold"
            >
              X
            </button>
          </div>
        </div>
        
        {/* Window content - classic Windows white/gray content area */}
        <div className="bg-gray-100 border-l border-r border-gray-300">
          <div className="p-4 flex items-start">
            <div className="flex-shrink-0 mr-4">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                <span className="text-white text-3xl font-bold">!</span>
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-700 mb-2">WARNING: Virus Detected!</h2>
              <p className="text-sm mb-2 text-gray-900">Your computer may be at risk. We have detected multiple viruses that may be stealing your banking details.</p>
              <p className="text-sm mb-4 font-bold text-gray-900">Enter your credit card information to activate premium antivirus protection.</p>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="px-4 pb-4">
            <div className="mb-3">
              <label className="block text-sm font-bold mb-1 text-gray-900">Name on Card:</label>
              <input 
                type="text" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-400 p-2 bg-white text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="mb-3">
              <label className="block text-sm font-bold mb-1 text-gray-900">Card Number:</label>
              <input 
                type="text" 
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value.replace(/[^\d-]/g, '').slice(0, 19))}
                className="w-full border border-gray-400 p-2 bg-white text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="1234-5678-9012-3456"
                required
              />
            </div>
            
            <div className="flex space-x-4 mb-4">
              <div className="flex-1">
                <label className="block text-sm font-bold mb-1 text-gray-900">Expiry Date:</label>
                <input 
                  type="text" 
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value.replace(/[^\d/]/g, '').slice(0, 5))}
                  className="w-full border border-gray-400 p-2 bg-white text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="MM/YY"
                  required
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-bold mb-1 text-gray-900">Security Code:</label>
                <input 
                  type="text" 
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  className="w-full border border-gray-400 p-2 bg-white text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="123"
                  required
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button 
                onClick={handleCloseClick}
                type="button"
                className="px-4 py-2 bg-gradient-to-b from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 border border-gray-400 text-sm font-bold text-gray-900"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="px-4 py-2 bg-gradient-to-b from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 border border-blue-900 text-white text-sm font-bold"
              >
                Activate Protection
              </button>
            </div>
          </form>
        </div>
        
        {/* Window footer - classic Windows status bar */}
        <div className="bg-gray-200 border border-gray-300 px-2 py-1 text-xs flex justify-between items-center">
          <span className="text-gray-700">© Windows Security Center</span>
          <span className="text-red-600 font-bold">Status: Critical - Immediate Action Required</span>
        </div>
      </div>
      
      {/* Second popup that appears when trying to close */}
      {showSecondPopup && (
        <div 
          className="w-[400px] shadow-xl overflow-hidden"
          style={{ 
            position: 'absolute',
            top: '40%',
            left: '60%',
            zIndex: 52 
          }}
        >
          <div className="flex items-center justify-between bg-gradient-to-r from-blue-700 to-blue-500 px-2 py-1 text-white select-none cursor-default">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-yellow-500 mr-2 rounded-full"></div>
              <span className="text-sm font-bold">System Warning</span>
            </div>
            <button 
              onClick={onClose}
              className="w-5 h-5 flex items-center justify-center mx-1 bg-gradient-to-b from-red-400 to-red-600 hover:from-red-500 hover:to-red-700 border border-red-700 text-white font-bold"
            >
              X
            </button>
          </div>
          
          <div className="bg-gray-100 p-4 border-l border-r border-b border-gray-300">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center mr-3">
                <span className="text-black text-xl font-bold">!</span>
              </div>
              <p className="text-sm font-bold text-gray-900">Are you sure you want to leave?</p>
            </div>
            <p className="text-xs mb-4 text-gray-900">Your computer is still at risk. If you leave now, your personal data may be compromised.</p>
            
            <div className="flex justify-end space-x-2">
              <button 
                onClick={() => setShowSecondPopup(false)}
                className="px-4 py-2 bg-gradient-to-b from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 border border-gray-400 text-sm font-bold text-gray-900"
              >
                Go Back
              </button>
              <button 
                onClick={onClose}
                className="px-4 py-2 bg-gradient-to-b from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 border border-blue-900 text-white text-sm font-bold"
              >
                Exit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Third popup - even more annoying */}
      {showThirdPopup && (
        <div 
          className="w-[350px] shadow-xl overflow-hidden"
          style={{ 
            position: 'absolute',
            top: '30%',
            left: '30%',
            zIndex: 53 
          }}
        >
          <div className="flex items-center justify-between bg-gradient-to-r from-red-700 to-red-500 px-2 py-1 text-white select-none cursor-default">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-white mr-2 rounded-full"></div>
              <span className="text-sm font-bold">CRITICAL ERROR</span>
            </div>
            <button 
              onClick={onClose}
              className="w-5 h-5 flex items-center justify-center mx-1 bg-gradient-to-b from-red-400 to-red-600 hover:from-red-500 hover:to-red-700 border border-red-700 text-white font-bold"
            >
              X
            </button>
          </div>
          
          <div className="bg-gray-100 p-4 border-l border-r border-b border-gray-300">
            <p className="text-center font-bold text-red-700 text-sm mb-3">SYSTEM FAILURE IMMINENT</p>
            <p className="text-xs mb-3 text-gray-900">Hard drive corruption detected. Contact technical support immediately:</p>
            <p className="text-center font-bold text-sm mb-4 text-gray-900">1-800-FAKE-SUPPORT</p>
            
            <div className="flex justify-center">
              <button 
                onClick={onClose}
                className="px-4 py-2 bg-gradient-to-b from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 border border-green-900 text-white text-sm font-bold"
              >
                Call Support
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScamPopup; 